// src/types/lifeDimension.ts
// Life Dimensions 系統的類型定義
//
// 設計理念：
// - Life Dimensions 使用與 Objectives 相同的 8 個分類
// - Objectives 是「有意圖的目標」，Life Dimensions 是「生活的完整維度」
// - 即使用戶沒有在某個維度設定 Objective，該維度仍然存在並可累積 Ripples
// - 確保生活平衡：所有 8 個維度都被追蹤和可視化

import { Timestamp } from 'firebase/firestore';
import { ObjectiveCategory, OBJECTIVE_CATEGORY_CONFIG } from '../features/goal/types/objective';

/**
 * Life Dimension 類型（等同於 ObjectiveCategory，但不含 legacy 的 'family'）
 */
export type LifeDimension = Exclude<ObjectiveCategory, 'family'>;

/**
 * 所有 Life Dimensions 列表（固定 8 個）
 */
export const ALL_LIFE_DIMENSIONS: LifeDimension[] = [
  'work',
  'health',
  'growth',
  'finance',
  'romance',
  'social',
  'leisure',
  'environment'
];

/**
 * 單個維度的數據
 */
export interface DimensionData {
  totalRipples: number;        // 累積漣漪值
  weeklyRipples: number[];     // 每週漣漪值（最多12週）
  currentWeek: number;         // 當前是第幾週（1-12+）
  lastProgressAt?: Timestamp;  // 最後推進時間
}

/**
 * Life Dimensions 文檔結構
 *
 * 儲存位置：lifeDimensions/{userId}
 * 每個用戶一筆文檔，包含所有 8 個維度的數據
 */
export interface LifeDimensions {
  userId: string;
  dimensions: {
    work: DimensionData;
    health: DimensionData;
    growth: DimensionData;
    finance: DimensionData;
    romance: DimensionData;
    social: DimensionData;
    leisure: DimensionData;
    environment: DimensionData;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * 創建空白的 DimensionData
 */
export const createEmptyDimensionData = (): DimensionData => ({
  totalRipples: 0,
  weeklyRipples: [],
  currentWeek: 1,
  lastProgressAt: undefined
});

/**
 * 創建空白的 LifeDimensions（用於新用戶初始化）
 */
export const createEmptyLifeDimensions = (userId: string): Omit<LifeDimensions, 'createdAt' | 'updatedAt'> => ({
  userId,
  dimensions: {
    work: createEmptyDimensionData(),
    health: createEmptyDimensionData(),
    growth: createEmptyDimensionData(),
    finance: createEmptyDimensionData(),
    romance: createEmptyDimensionData(),
    social: createEmptyDimensionData(),
    leisure: createEmptyDimensionData(),
    environment: createEmptyDimensionData()
  }
});

/**
 * Life Dimension 配置（從 OBJECTIVE_CATEGORY_CONFIG 繼承）
 */
export const LIFE_DIMENSION_CONFIG = Object.fromEntries(
  ALL_LIFE_DIMENSIONS.map(dimension => [
    dimension,
    OBJECTIVE_CATEGORY_CONFIG[dimension]
  ])
) as Record<LifeDimension, typeof OBJECTIVE_CATEGORY_CONFIG[LifeDimension]>;

/**
 * 更新單個維度的 Ripples
 */
export interface UpdateDimensionRipplesRequest {
  dimension: LifeDimension;
  ripplesToAdd: number;
  date?: Date;  // 用於週進度計算，預設為當天
}

/**
 * 查詢結果：單個維度的摘要
 */
export interface DimensionSummary {
  dimension: LifeDimension;
  totalRipples: number;
  currentWeek: number;
  lastProgressAt?: Timestamp;
}
